import type { QueryFilter } from '@domain/schemas/filters.schema';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';

import { deriveQueryFilters } from './utils/derive-query-filters';
import { toFilterRows } from './utils/filters';
import type { FilterRow, OptionsRecord } from './types';

interface QueryBuilderState {
  rowsByEntity: Record<string, FilterRow[]>;
  optionsRecordByEntity: Record<string, OptionsRecord>;
  panelOpenByEntity: Record<string, boolean>;
  addRow: (entityName: string) => void;
  removeRow: (entityName: string, rowId: string) => void;
  updateRow: (entityName: string, rowId: string, patch: Partial<FilterRow>) => void;
  clearRows: (entityName: string) => void;
  initRows: (entityName: string, rows: FilterRow[]) => void;
  setOptionsRecord: (entityName: string, record: OptionsRecord) => void;
  setPanelOpen: (entityName: string, open: boolean) => void;
}

const emptyRow = (): FilterRow => ({ id: uuidv4(), field: '', comparator: '', values: [] });

export const useQueryBuilderStore = create<QueryBuilderState>((set) => ({
  rowsByEntity: {},
  optionsRecordByEntity: {},
  panelOpenByEntity: {},

  addRow: (entityName) =>
    set((state) => ({
      rowsByEntity: {
        ...state.rowsByEntity,
        [entityName]: [...(state.rowsByEntity[entityName] ?? []), emptyRow()],
      },
    })),

  removeRow: (entityName, rowId) =>
    set((state) => {
      const remaining = (state.rowsByEntity[entityName] ?? []).filter((r) => r.id !== rowId);
      return {
        rowsByEntity: {
          ...state.rowsByEntity,
          [entityName]: remaining.length > 0 ? remaining : [emptyRow()],
        },
      };
    }),

  updateRow: (entityName, rowId, patch) =>
    set((state) => {
      return {
        rowsByEntity: {
          ...state.rowsByEntity,
          [entityName]: (state.rowsByEntity[entityName] ?? []).map((r) => {
            if (r.id === rowId) {
              const newComparatorIsRel = patch.comparator === 'rel';
              const prevComparatorIsRel = r.comparator === 'rel';

              // If the comparator is changing from or to REL, clear the values
              if (patch.comparator && newComparatorIsRel != prevComparatorIsRel) {
                return { ...r, ...patch, values: [] };
              }
              return { ...r, ...patch };
            }
            return r;
          }),
        },
      };
    }),

  clearRows: (entityName) =>
    set((state) => ({
      rowsByEntity: { ...state.rowsByEntity, [entityName]: [emptyRow()] },
    })),

  initRows: (entityName, rows) =>
    set((state) => ({
      rowsByEntity: { ...state.rowsByEntity, [entityName]: rows },
    })),

  setOptionsRecord: (entityName, record) =>
    set((state) => ({
      optionsRecordByEntity: { ...state.optionsRecordByEntity, [entityName]: record },
    })),

  setPanelOpen: (entityName, open) =>
    set((state) => ({
      panelOpenByEntity: { ...state.panelOpenByEntity, [entityName]: open },
    })),
}));

// Deliberately leaves panel open-state alone — it defaults to collapsed on navigation (CB-2993).
export const applyQueryFilters = (entityName: string, filters: QueryFilter[]) => {
  const { initRows, clearRows } = useQueryBuilderStore.getState();
  if (filters.length > 0) {
    initRows(entityName, toFilterRows(filters));
  } else {
    clearRows(entityName);
  }
};

export const resetQueryFilters = (entityName: string, defaultFilters: QueryFilter[] = []) => {
  const { initRows, clearRows } = useQueryBuilderStore.getState();
  if (defaultFilters.length > 0) initRows(entityName, toFilterRows(defaultFilters));
  else clearRows(entityName);
};

export const getQueryFilters = (entityName: string): QueryFilter[] =>
  deriveQueryFilters(useQueryBuilderStore.getState().rowsByEntity[entityName] ?? []);

export const useQueryFilters = (entityName: string): QueryFilter[] => {
  const rows = useQueryBuilderStore((s) => s.rowsByEntity[entityName] ?? []);
  return deriveQueryFilters(rows);
};
